import { useQueryClient } from '@tanstack/react-query'
import { CheckIcon, Clock, Trash2, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import { Badge, Button, ButtonGroup, Modal } from 'slab'
import { snakeCaseToTitleCase } from 'utils'
import {
  getGetAdminEmployersEmployerIdComplianceAuditsQueryKey,
  useDeleteAdminEmployersEmployerIdComplianceAuditsAuditId,
} from 'utils/web_api'
import { type AdminComplianceAudit, type AdminRuleResult } from 'utils/web_api.schemas'

interface RuleResultDisplayProps {
  ruleResults: AdminRuleResult[]
  title: string
  showAsPending?: boolean
}

const RuleResultDisplay = ({ ruleResults, title, showAsPending = false }: RuleResultDisplayProps) => {
  if (ruleResults.length === 0) {
    return null
  }

  return (
    <div className='space-y-2'>
      <h4 className='text-sm font-medium text-gray-700'>{title}</h4>
      <div className='space-y-1'>
        {ruleResults.map((ruleResult, index) => (
          <div key={index} className='flex items-center space-x-2'>
            {showAsPending
              ? (
                <Clock className='h-4 w-4 text-yellow-500' />
                )
              : ruleResult.passes
                ? (
                  <CheckIcon className='h-4 w-4 text-green-500' />
                  )
                : (
                  <X className='h-4 w-4 text-red-500' />
                  )}
            <span className='text-sm text-gray-600 capitalize'>
              {ruleResult.rule.replace(/_/g, ' ')}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

interface AuditCardProps {
  audit: AdminComplianceAudit
  employerId: string
  permittedAttributes?: string[]
  className?: string
}

const AuditCard = ({ audit, employerId, permittedAttributes, className }: AuditCardProps) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const queryClient = useQueryClient()

  const canModifyComplianceAudit = permittedAttributes?.includes('compliance_audit') ?? false

  const deleteMutation = useDeleteAdminEmployersEmployerIdComplianceAuditsAuditId({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetAdminEmployersEmployerIdComplianceAuditsQueryKey(employerId),
        })
        toast.success('Bank partner removed successfully')
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to remove bank partner'
        toast.error(errorMessage)
      },
    },
  })

  const getStateBadgeType = (state: string) => {
    switch (state) {
      case 'pending':
        return 'warning'
      case 'passed':
        return 'success'
      case 'failed':
        return 'critical'
      case 'cancelled':
        return 'neutral'
      default:
        return 'neutral'
    }
  }

  // Show requirements as pending only if not all progress rules are passing
  const showRequirementsAsPending = !(audit.progress_rule_results.every(rule => rule.passes))

  const handleDelete = () => {
    setIsDeleteModalOpen(false)
    deleteMutation.mutate({
      employerId,
      auditId: audit.id,
    })
  }

  return (
    <>
      <div className={`bg-white border border-gray-200 rounded-lg p-4 shadow-sm ${className ?? ''}`}>
        <div className='flex items-center justify-between mb-3'>
          <h3 className='text-lg font-medium text-gray-900'>{audit.partner_name}</h3>
          <div className='flex items-center gap-2'>
            <Badge type={getStateBadgeType(audit.state)}>
              {snakeCaseToTitleCase(audit.state)}
            </Badge>
            <DisplayTooltipOnHover
              text={canModifyComplianceAudit ? 'Delete bank partner' : 'You do not have permission to delete this bank partner'}
              asChild
            >
              <button
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={!canModifyComplianceAudit}
                className='p-1 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-gray-400'
                aria-label='Delete bank partner'
              >
                <Trash2 className='h-4 w-4' />
              </button>
            </DisplayTooltipOnHover>
          </div>
        </div>
        <div className='space-y-4'>
          <RuleResultDisplay
            ruleResults={audit.final_rule_results}
            title='Requirements'
            showAsPending={showRequirementsAsPending}
          />
          <RuleResultDisplay
            ruleResults={audit.progress_rule_results}
            title='Progress'
          />
        </div>
      </div>

      <Modal open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <Modal.Body>
          <Modal.Title>Removing Bank Partner</Modal.Title>
          <Modal.Description>
            This will impact the financial partner this employer will be onboarded onto. Please confirm this is necessary before moving forward.
          </Modal.Description>
        </Modal.Body>
        <Modal.Footer>
          <ButtonGroup align='right'>
            <Button
              variant='secondary'
              size='large'
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant='primary-destructive'
              size='large'
              leftIcon={Trash2}
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Removing...' : 'Confirm'}
            </Button>
          </ButtonGroup>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default AuditCard
